import React from 'react';

// Reuse the existing world-map vector data (ISO alpha-3 `code` → SVG path `d`)
// from the config-driven countries module rather than duplicating 1000+ paths.
import countriesData from '@/components/modules/countries/assets/countries';
import { Frame, Svg, Zoom } from './WorldMap.styles';

type CountryShape = { id: string; shape: string };
const COUNTRIES = countriesData as CountryShape[];

/** SVG canvas of the source map. */
const VIEW_W = 2000;
const VIEW_H = 1001;

const IDENTITY = 'translate(0px, 0px) scale(1)';

/** Fraction of the frame the selected country should fill, and the zoom clamp. */
const FIT = 0.55;
const MIN_SCALE = 1;
const MAX_SCALE = 16;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** A country's tint on a map that colours countries by status (Settings → Country access). */
export type MapTone = 'allowed' | 'blocked';

export type WorldMapProps = {
  /** Alpha-3 id of the country to tint and zoom to (the operating-region step); `null` shows the whole world. */
  selected: string | null;
  /**
   * Optional tone per shape id (alpha-3), painted as the `allowed` / `blocked` class on the country's path. The map
   * carries no colours for them: the caller styles those classes (e.g. `styled(WorldMap)`).
   */
  tones?: Readonly<Record<string, MapTone>>;
  /** Lets a caller restyle the frame and its paths via `styled(WorldMap)`. */
  className?: string;
};

/**
 * Dimmed world map for the operating-region step. Renders every country as a
 * faint path inside a clipped, bordered frame; the selected region is tinted and
 * the map smoothly zooms to it.
 *
 * Centering is derived from the selected path's own `getBBox()` (not a hardcoded
 * focal table), so EVERY country — Mexico included — is framed dead-centre and
 * scaled to fit, and the result can't drift as the data changes.
 */
export const WorldMap = ({ selected, tones, className }: WorldMapProps): React.ReactElement => {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [transform, setTransform] = React.useState(IDENTITY);

  React.useLayoutEffect(() => {
    if (!selected) {
      setTransform(IDENTITY);
      return;
    }
    const svg = svgRef.current;
    const path = svg?.querySelector<SVGPathElement>(`[data-code="${selected}"]`);
    if (!path) {
      setTransform(IDENTITY);
      return;
    }
    let box: DOMRect;
    try {
      box = path.getBBox();
    } catch {
      setTransform(IDENTITY);
      return;
    }
    if (box.width <= 0 || box.height <= 0) {
      setTransform(IDENTITY);
      return;
    }
    // Scale so the country fills ~FIT of the frame on its tighter axis, clamped.
    const scale = clamp(Math.min((VIEW_W * FIT) / box.width, (VIEW_H * FIT) / box.height), MIN_SCALE, MAX_SCALE);
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    const tx = VIEW_W / 2 - scale * cx;
    const ty = VIEW_H / 2 - scale * cy;
    setTransform(`translate(${tx}px, ${ty}px) scale(${scale})`);
  }, [selected]);

  return (
    <Frame className={className}>
      <Svg ref={svgRef} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <Zoom $transform={transform}>
          {COUNTRIES.map((c) => (
            <path
              key={c.id}
              data-code={c.id}
              d={c.shape}
              className={selected === c.id ? 'selected' : tones?.[c.id]}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </Zoom>
      </Svg>
    </Frame>
  );
};

export default WorldMap;